import { Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { KuukausiAikavali, LocalDate, NumberMonth } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { AlvIlmoitusjakso, HOLVI_ZEN_KIRJANPITAJA, KirjanpitoKuukausiruutu, ListausAsiakas } from '../model/asiakas'
import { AlvPalluranTila, EraantymisenTila, KORTTIEN_ALKU, Korttisuodatus, TilinpaatoskortinRivi, TyolistanYhteenvetokortit, YhteenvetokortinRivi } from '../model/tyolistan-yhteenvetokortit'
import { KuukausiruudunTilatService } from './kuukausiruudun-tilat.service'

const LASKETTAVAT_ALV_TILAT: AlvPalluranTila[] = ['sin', 'ora', 'mus', 'pun']
const ERAANTYMISEN_TILOJEN_VAHVUUS: Record<EraantymisenTila, number> = { normaali: 0, eraantyy: 1, myohassa: 2 }
const ALV_JAKSOJEN_JARJESTYS: AlvIlmoitusjakso[] = [AlvIlmoitusjakso.KK1, AlvIlmoitusjakso.KK3, AlvIlmoitusjakso.KK12]

export class TyolistanYhteenvetokortitService {
  constructor(
    private _dateService: DateService,
    private _kuukausiruudunTilatService: KuukausiruudunTilatService,
  ) {}

  public laske(asiakkaat: ListausAsiakas[], annaRuutu: (asiakasAvain: string, kuukausi: NumberMonth) => KirjanpitoKuukausiruutu | undefined, nyt: Date): TyolistanYhteenvetokortit {
    const tanaan = this._dateService.dateToLocalDate(nyt)
    const kuluvaKuukausi = this._dateService.localMonthToNumber(this._dateService.dateToLocalMonth(nyt))

    const kuukaudet: NumberMonth[] = []
    for (let k = KORTTIEN_ALKU; k <= kuluvaKuukausi; k = this._dateService.lisaaKuukausiaKuukaudenNumero(k, 1)) {
      kuukaudet.push(k)
    }

    const kirjaukset = new Map<string, YhteenvetokortinRivi>()
    const kirjanpidot = new Map<string, YhteenvetokortinRivi>()
    const alvt = new Map<string, YhteenvetokortinRivi>()
    const alvRivienJaksot = new Map<string, AlvIlmoitusjakso>()
    const veroilmoitukset = new Map<string, YhteenvetokortinRivi>()
    const veroilmoitusrivienYritysmuodot = new Map<string, string>()
    const tilinpaatokset = new Map<string, TilinpaatoskortinRivi>()

    for (const asiakas of asiakkaat) {
      const onkoHolvi = asiakas.p === HOLVI_ZEN_KIRJANPITAJA
      // Vain uudella ruudukkologiikalla piirretyt asiakkaat. Holvin sijaisasiakkaat piirretään vanhalla logiikalla.
      if (!asiakas.u && !onkoHolvi) {
        continue
      }
      // Työkiellossa olevalle asiakkaalle ei saa tehdä töitä.
      if (asiakas.tk) {
        continue
      }

      for (const k of kuukaudet) {
        const ruutu = annaRuutu(asiakas.k, k)
        if (!ruutu) {
          continue
        }

        const onPaattynytKuukausi = k < kuluvaKuukausi
        if (onPaattynytKuukausi && ruutu.u > 0) {
          const rivi = this._annaRivi(kirjaukset, `kirjaus|${k}`, () => ({ avain: `kirjaus|${k}`, kuukausi: k, nimi: this._annaJaksonNimi(k), asiakasAvaimet: [], kirjauksia: 0 }))
          rivi.asiakasAvaimet.push(asiakas.k)
          rivi.kirjauksia += ruutu.u
        }

        if (onPaattynytKuukausi && !ruutu.kp && this._kuukausiruudunTilatService.onkoKirjanpitoTehtavissa(ruutu, onkoHolvi)) {
          const rivi = this._annaRivi(kirjanpidot, `kirjanpito|${k}`, () => ({ avain: `kirjanpito|${k}`, kuukausi: k, nimi: this._annaJaksonNimi(k), asiakasAvaimet: [] }))
          rivi.asiakasAvaimet.push(asiakas.k)
        }

        this._lisaaAlvRivi(alvt, alvRivienJaksot, asiakas, ruutu, annaRuutu, nyt, tanaan)

        const loppu = this._kuukausiruudunTilatService.annaTilikaudenLoppu(ruutu)
        if (!loppu || !this._dateService.compareLocalDates(loppu, '<', tanaan)) {
          continue
        }
        this._lisaaVeroilmoitusrivi(veroilmoitukset, veroilmoitusrivienYritysmuodot, asiakas, ruutu, loppu, tanaan)
        this._lisaaTilinpaatosrivi(tilinpaatokset, asiakas, ruutu, loppu, tanaan)
      }
    }

    const kirjausrivit = this._jarjestaAsiakkaat([...kirjaukset.values()]).sort((a, b) => a.kuukausi - b.kuukausi)
    const kirjanpitorivit = this._jarjestaAsiakkaat([...kirjanpidot.values()]).sort((a, b) => a.kuukausi - b.kuukausi)
    const alvRivit = this._jarjestaAsiakkaat([...alvt.values()]).sort(
      (a, b) =>
        this._vertaaErapaivia(a.erapaiva, b.erapaiva) ||
        this._annaAlvJaksonJarjestys(alvRivienJaksot.get(a.avain)) - this._annaAlvJaksonJarjestys(alvRivienJaksot.get(b.avain)) ||
        a.kuukausi - b.kuukausi,
    )
    const veroilmoitusrivit = this._jarjestaAsiakkaat([...veroilmoitukset.values()]).sort(
      (a, b) => this._vertaaErapaivia(a.erapaiva, b.erapaiva) || a.kuukausi - b.kuukausi || veroilmoitusrivienYritysmuodot.get(a.avain).localeCompare(veroilmoitusrivienYritysmuodot.get(b.avain)),
    )
    const tilinpaatosrivit = [...tilinpaatokset.values()].sort((a, b) => this._vertaaErapaivia(a.erapaiva, b.erapaiva) || a.kuukausi - b.kuukausi)
    for (const rivi of tilinpaatosrivit) {
      for (const avaimet of Object.values(rivi.sarakkeet)) {
        avaimet.sort()
      }
    }

    return {
      kirjaukset: { yhteensa: kirjausrivit.reduce((summa, rivi) => summa + rivi.kirjauksia, 0), rivit: kirjausrivit },
      kirjanpidot: { yhteensa: this._laskeAsiakkaat(kirjanpitorivit), rivit: kirjanpitorivit },
      alv: { yhteensa: this._laskeAsiakkaat(alvRivit), rivit: alvRivit },
      veroilmoitukset: { yhteensa: this._laskeAsiakkaat(veroilmoitusrivit), rivit: veroilmoitusrivit },
      tilinpaatokset: {
        yhteensa: tilinpaatosrivit.reduce((summa, rivi) => summa + rivi.sarakkeet.tekematta.length + rivi.sarakkeet.allekirjoittamatta.length + rivi.sarakkeet.rekisteroimatta.length, 0),
        rivit: tilinpaatosrivit,
      },
    }
  }

  public suodataKorttisuodatuksella<T extends { k: string }>(asiakkaat: T[], kortit: TyolistanYhteenvetokortit | null, suodatus: Korttisuodatus | null): T[] {
    if (!suodatus) {
      return asiakkaat
    }
    const avaimet = new Set(this._annaKorttisuodatuksenAsiakasAvaimet(kortit, suodatus))
    return asiakkaat.filter(asiakas => avaimet.has(asiakas.k))
  }

  public annaKorttisuodatuksenAsiakasmaara(kortit: TyolistanYhteenvetokortit | null, suodatus: Korttisuodatus): number {
    return this._annaKorttisuodatuksenAsiakasAvaimet(kortit, suodatus).length
  }

  public annaKorttisuodatuksenAikavali(jakso: NumberMonth): KuukausiAikavali {
    return {
      start: this._dateService.numberToLocalMonth(this._dateService.lisaaKuukausiaKuukaudenNumero(jakso, -6)),
      end: this._dateService.numberToLocalMonth(this._dateService.lisaaKuukausiaKuukaudenNumero(jakso, 3)),
    }
  }

  public annaRuutuhaunRajat(valittu: KuukausiAikavali, nyt: Date, laajenna: boolean): { alku: NumberMonth; loppu: NumberMonth } {
    const valitunAlku = this._dateService.localMonthToNumber(valittu.start)
    const valitunLoppuJaSeuraava = this._dateService.lisaaKuukausiaKuukaudenNumero(this._dateService.localMonthToNumber(valittu.end), 1)
    if (!laajenna) {
      return { alku: valitunAlku, loppu: valitunLoppuJaSeuraava }
    }
    const kuluvaKuukausi = this._dateService.localMonthToNumber(this._dateService.dateToLocalMonth(nyt))
    // −6 / +3 kk kattaa korttiklikkauksen kuukausivälit ja +1 seuraavan kuukauden ruudun.
    const vanhimmanKorttivalinAlku = this._dateService.lisaaKuukausiaKuukaudenNumero(KORTTIEN_ALKU, -6)
    const uusimmanKorttivalinLoppuJaSeuraava = this._dateService.lisaaKuukausiaKuukaudenNumero(kuluvaKuukausi, 4)
    return { alku: Math.min(valitunAlku, vanhimmanKorttivalinAlku), loppu: Math.max(valitunLoppuJaSeuraava, uusimmanKorttivalinLoppuJaSeuraava) }
  }

  private _lisaaAlvRivi(
    rivit: Map<string, YhteenvetokortinRivi>,
    rivienJaksot: Map<string, AlvIlmoitusjakso>,
    asiakas: ListausAsiakas,
    ruutu: KirjanpitoKuukausiruutu,
    annaRuutu: (asiakasAvain: string, kuukausi: NumberMonth) => KirjanpitoKuukausiruutu | undefined,
    nyt: Date,
    tanaan: LocalDate,
  ) {
    // Ruudukko korvaa puuttuvan seuraavan ruudun ruudulla, jonka jakso on tuntematon, jolloin 3 kk:n ja 12 kk:n jakso tulkitaan katkenneeksi.
    const seuraavanRuudunAlv = annaRuutu(asiakas.k, this._dateService.lisaaKuukausiaKuukaudenNumero(ruutu.k, 1))?.alv ?? AlvIlmoitusjakso.TUNTEMATON
    const tila = this._kuukausiruudunTilatService.annaAlvPalluranTila(ruutu, seuraavanRuudunAlv, nyt)
    if (!LASKETTAVAT_ALV_TILAT.includes(tila)) {
      return
    }
    const rajat = this._kuukausiruudunTilatService.annaAlvRajat(ruutu, seuraavanRuudunAlv)
    const avain = `alv|${ruutu.k}|${ruutu.alv}|${this._dateService.localDateToNumber(rajat.erapaiva)}`
    const rivi = this._annaRivi(rivit, avain, () => {
      const jakso = this._annaAlvJaksonNimi(ruutu.alv)
      const paivamaaranTila = this._kuukausiruudunTilatService.annaEraantymisenTila(rajat.erapaiva, tanaan)
      return {
        avain,
        kuukausi: ruutu.k,
        nimi: jakso ? `${this._annaJaksonNimi(ruutu.k)} · ${jakso}` : this._annaJaksonNimi(ruutu.k),
        asiakasAvaimet: [],
        erapaiva: rajat.erapaiva,
        // Myöhässä vain mustan palluran perusteella: uudelleenlähetettävä (punainen) ei muutu ruudukossa mustaksi.
        eraantymisenTila: paivamaaranTila === 'myohassa' ? 'normaali' : paivamaaranTila,
      }
    })
    if (tila === 'mus') {
      rivi.eraantymisenTila = 'myohassa'
    }
    rivienJaksot.set(avain, ruutu.alv)
    rivi.asiakasAvaimet.push(asiakas.k)
  }

  private _lisaaVeroilmoitusrivi(
    rivit: Map<string, YhteenvetokortinRivi>,
    rivienYritysmuodot: Map<string, string>,
    asiakas: ListausAsiakas,
    ruutu: KirjanpitoKuukausiruutu,
    loppu: LocalDate,
    tanaan: LocalDate,
  ) {
    // Tarkoituksella eri sääntö kuin tilikauden reunavärissä, joka kuvaa tilikauden sulkemista eikä veroilmoitusta.
    if (ruutu.vp || ruutu.bkt || ruutu.bd) {
      return
    }
    const erapaiva = this._kuukausiruudunTilatService.annaVeroilmoituksenErapaiva(asiakas.c, loppu)
    const avain = `vero|${ruutu.k}|${asiakas.c}|${this._dateService.localDateToNumber(erapaiva)}`
    const yritysmuoto = this._annaYritysmuodonLyhenne(asiakas.c)
    const rivi = this._annaRivi(rivit, avain, () => ({
      avain,
      kuukausi: ruutu.k,
      nimi: `${this._annaJaksonNimi(ruutu.k)} · ${yritysmuoto}`,
      asiakasAvaimet: [],
      erapaiva,
      eraantymisenTila: this._kuukausiruudunTilatService.annaEraantymisenTila(erapaiva, tanaan),
    }))
    rivienYritysmuodot.set(avain, yritysmuoto)
    rivi.asiakasAvaimet.push(asiakas.k)
  }

  private _lisaaTilinpaatosrivi(rivit: Map<string, TilinpaatoskortinRivi>, asiakas: ListausAsiakas, ruutu: KirjanpitoKuukausiruutu, loppu: LocalDate, tanaan: LocalDate) {
    const vaihe = this._kuukausiruudunTilatService.annaTilinpaatoksenVaihe(ruutu, asiakas.c)
    if (!vaihe && !ruutu.oor) {
      return
    }
    const erapaiva = this._kuukausiruudunTilatService.annaRekisteroinninErapaiva(loppu)
    const avain = `tp|${ruutu.k}|${this._dateService.localDateToNumber(erapaiva)}`
    const rivi = this._annaRivi(rivit, avain, () => ({
      avain,
      kuukausi: ruutu.k,
      nimi: this._annaJaksonNimi(ruutu.k),
      erapaiva,
      eraantymisenTila: this._kuukausiruudunTilatService.annaEraantymisenTila(erapaiva, tanaan),
      osinkojenEraantymisenTila: 'normaali',
      sarakkeet: { tekematta: [], allekirjoittamatta: [], rekisteroimatta: [], osingot: [] },
    }))
    if (vaihe) {
      rivi.sarakkeet[vaihe].push(asiakas.k)
    }
    if (ruutu.oor) {
      rivi.sarakkeet.osingot.push(asiakas.k)
      const osinkojenErapaiva = this._kuukausiruudunTilatService.annaOsinkoilmoituksenErapaiva(ruutu.onp)
      if (osinkojenErapaiva) {
        const tila = this._kuukausiruudunTilatService.annaEraantymisenTila(osinkojenErapaiva, tanaan)
        if (ERAANTYMISEN_TILOJEN_VAHVUUS[tila] > ERAANTYMISEN_TILOJEN_VAHVUUS[rivi.osinkojenEraantymisenTila]) {
          rivi.osinkojenEraantymisenTila = tila
        }
      }
    }
  }

  private _annaKorttisuodatuksenAsiakasAvaimet(kortit: TyolistanYhteenvetokortit | null, suodatus: Korttisuodatus): string[] {
    if (!kortit) {
      return []
    }
    if (suodatus.kortti === 'tilinpaatokset') {
      const rivi = kortit.tilinpaatokset.rivit.find(r => r.avain === suodatus.rivinAvain)
      return (suodatus.sarake && rivi?.sarakkeet[suodatus.sarake]) ?? []
    }
    return kortit[suodatus.kortti].rivit.find(r => r.avain === suodatus.rivinAvain)?.asiakasAvaimet ?? []
  }

  private _annaRivi<R>(rivit: Map<string, R>, avain: string, luo: () => R): R {
    let rivi = rivit.get(avain)
    if (!rivi) {
      rivi = luo()
      rivit.set(avain, rivi)
    }
    return rivi
  }

  private _jarjestaAsiakkaat(rivit: YhteenvetokortinRivi[]) {
    for (const rivi of rivit) {
      rivi.asiakasAvaimet.sort()
    }
    return rivit
  }

  private _laskeAsiakkaat(rivit: YhteenvetokortinRivi[]) {
    return rivit.reduce((summa, rivi) => summa + rivi.asiakasAvaimet.length, 0)
  }

  private _vertaaErapaivia(a: LocalDate, b: LocalDate) {
    return this._dateService.localDateToNumber(a) - this._dateService.localDateToNumber(b)
  }

  private _annaAlvJaksonJarjestys(jakso: AlvIlmoitusjakso) {
    const indeksi = ALV_JAKSOJEN_JARJESTYS.indexOf(jakso)
    return indeksi === -1 ? ALV_JAKSOJEN_JARJESTYS.length : indeksi
  }

  private _annaJaksonNimi(kuukausi: NumberMonth) {
    const kk = this._dateService.numberToLocalMonth(kuukausi)
    return `${kk.year}/${String(kk.month).padStart(2, '0')}`
  }

  private _annaAlvJaksonNimi(jakso: AlvIlmoitusjakso) {
    switch (jakso) {
      case AlvIlmoitusjakso.KK1:
        return '1kk'
      case AlvIlmoitusjakso.KK3:
        return '3kk'
      case AlvIlmoitusjakso.KK12:
        return '12kk'
      default:
        return null
    }
  }

  private _annaYritysmuodonLyhenne(yritysmuoto: Yritysmuoto) {
    switch (yritysmuoto) {
      case Yritysmuoto.TOIMINIMI:
        return 'Tmi'
      case Yritysmuoto.AVOINYHTIO:
        return 'Ay'
      case Yritysmuoto.KOMMANDIITTIYHTIO:
        return 'Ky'
      case Yritysmuoto.OSAKEYHTIO:
        return 'Oy'
      case Yritysmuoto.OSUUSKUNTA:
        return 'Osk'
      case Yritysmuoto.YHDISTYS:
        return 'Ry'
      default:
        return 'Ei tied.'
    }
  }
}
